import { useId, useState, type FormEvent } from "react";
import type { MoneyAccount } from "@/api/cash";
import type { InvoiceSummary } from "@/api/invoices";
import type { Payment, PaymentInput, PaymentMethod } from "@/api/payments";
import type { CollectionInput, CollectionLineInput } from "@/api/receipts";
import {
  ArrivalFields,
  convertedAmount,
  paymentInput,
  type PaymentValues,
} from "@/components/forms/PaymentFields";
import { RupeeValue } from "@/components/forms/RupeeValue";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { formatMoney } from "@/lib/format";
import { centsToAmount } from "@/lib/money";
import { useFormValues, useSubmitState } from "@/lib/use-form";

/** Another open invoice of the same client, which the same money may settle too. */
type OpenInvoice = Pick<InvoiceSummary, "id" | "invoice_no" | "balance">;

type PaymentFormProps = {
  /** "record" a new payment, or complete the bank "details" of a recorded one. */
  mode: "record" | "details";
  currency: string;
  /** The invoice balance: the default amount of a new payment. */
  balance: string;
  initial?: Payment | null;
  /** The money accounts it may land in; the select appears only when given. */
  accounts?: MoneyAccount[];
  submitLabel: string;
  onSubmit: (input: PaymentInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
  /** Record mode: the client's other open invoices. Ticking one makes this one collection
   *  for several invoices, recorded on each and acknowledged on one receipt. */
  collect?: {
    invoiceId: string;
    others: OpenInvoice[];
    onSubmit: (input: CollectionInput) => Promise<unknown>;
  };
};

function cents(value: string): number {
  return Math.round(Number(value || "0") * 100);
}

export function PaymentForm({
  mode,
  currency,
  balance,
  initial,
  accounts,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
  collect,
}: PaymentFormProps) {
  const id = useId();
  const fixed = mode === "details";
  // Active accounts, plus an archived one the payment already sits in: bank details arrive
  // weeks later and must still save without moving the money.
  const accountOptions = accounts
    ?.filter((a) => !a.archived_at || a.id === initial?.received_into_id)
    .map((a) => ({
      value: a.id,
      label: `${a.name} (${a.currency}${a.archived_at ? ", archived" : ""})`,
    }));
  const { values, set, setValues } = useFormValues<PaymentValues>({
    received_on: initial?.received_on ?? todayIso(),
    amount: initial?.amount ?? balance,
    bank_charges: initial?.bank_charges ?? "0.00",
    method: (initial?.method ?? "bank_transfer") as PaymentMethod,
    received_currency: initial?.received_currency ?? "",
    received_amount: initial?.received_amount ?? "",
    received_rate: initial?.received_rate ? String(Number(initial.received_rate)) : "",
    received_by: initial?.received_by ?? "",
    received_place: initial?.received_place ?? "",
    reference: initial?.reference ?? "",
    received_into_id: fixed
      ? (initial?.received_into_id ?? "")
      : (accounts?.find((a) => a.is_default_in)?.id ?? ""),
    inr_credited: initial?.inr_credited ?? "",
    fx_rate: initial?.fx_rate ? String(Number(initial.fx_rate)) : "",
    fira_ref: initial?.fira_ref ?? "",
    purpose_code: initial?.purpose_code ?? "",
    notes: initial?.notes ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();

  const netCents = cents(values.amount) - cents(values.bank_charges);
  const net = Number.isFinite(netCents) && netCents > 0 ? centsToAmount(netCents) : null;
  const inr = Number(values.inr_credited);
  const impliedRate = net && inr > 0 ? (inr / Number(net)).toFixed(4) : null;
  const fixedHint = fixed ? "Fixed once recorded - void the payment to correct it" : undefined;
  const converted = convertedAmount(values);

  // The other invoices this money settles too: invoice id -> what it takes.
  const [alsoSettles, setAlsoSettles] = useState<Record<string, string>>({});
  const [waive, setWaive] = useState(false);
  const chosen = (collect?.others ?? []).filter((other) => other.id in alsoSettles);
  const multi = chosen.length > 0;
  const lines: CollectionLineInput[] = [
    { invoice_id: collect?.invoiceId ?? "", amount: values.amount },
    ...chosen.map((other) => ({ invoice_id: other.id, amount: alsoSettles[other.id] ?? "" })),
  ].map((line) => ({ ...line, amount: centsToAmount(cents(line.amount)) }));
  const takenCents = lines.reduce((sum, line) => sum + cents(line.amount), 0);
  // SAR 12,562 at 3.75 is USD 3,349.87, 13 cents short of the two invoices: the directors
  // may waive that, written off on the last invoice ticked.
  const shortCents = multi && converted ? takenCents - cents(converted) : 0;
  const lastOther = chosen.at(-1);
  // A write-off closes the invoice, so it is offered only when that invoice is being paid in
  // full - the server refuses one that would leave a balance owing.
  const canWaive =
    lastOther !== undefined &&
    shortCents > 0 &&
    cents(alsoSettles[lastOther.id] ?? "") === cents(lastOther.balance) &&
    shortCents < cents(lastOther.balance);
  const waived = waive && canWaive ? shortCents : 0;
  // The rupee value is of the whole sum: with other invoices ticked, all of them.
  const wholeNetCents = multi ? takenCents - waived - cents(values.bank_charges) : netCents;
  const wholeNet = wholeNetCents > 0 ? centsToAmount(wholeNetCents) : null;

  function toggle(other: OpenInvoice, on: boolean): void {
    setAlsoSettles((current) => {
      const rest = Object.fromEntries(Object.entries(current).filter(([key]) => key !== other.id));
      return on ? { ...rest, [other.id]: other.balance } : rest;
    });
  }

  function collection(payment: PaymentInput): CollectionInput {
    const settled = lines.map((line, index) =>
      index === lines.length - 1 && waived > 0
        ? {
            ...line,
            amount: centsToAmount(cents(line.amount) - waived),
            write_off: centsToAmount(waived),
          }
        : line,
    );
    return { payment: { ...payment, amount: centsToAmount(takenCents - waived) }, lines: settled };
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const input = paymentInput(values);
    void run(() => (collect && multi ? collect.onSubmit(collection(input)) : onSubmit(input)));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-received`}
          label="Received on"
          type="date"
          value={values.received_on}
          onChange={(value) => set("received_on", value)}
          error={errors.received_on}
          disabled={fixed}
          hint={fixedHint ?? "The date the money reached the bank account"}
        />
        <TextField
          id={`${id}-amount`}
          label={`Amount (${currency})`}
          inputMode="decimal"
          value={values.amount}
          onChange={(value) => set("amount", value)}
          error={errors.amount}
          disabled={fixed}
          hint={fixed ? undefined : `Balance ${formatMoney(balance, currency)}`}
        />
        <TextField
          id={`${id}-charges`}
          label={`Bank charges deducted (${currency})`}
          inputMode="decimal"
          value={values.bank_charges}
          onChange={(value) => set("bank_charges", value)}
          error={errors.bank_charges}
          disabled={fixed}
          hint="Taken by the banks on the way; still settles the invoice"
        />
      </div>
      <p className="text-sm">
        Net received: <strong>{net ? formatMoney(net, currency) : "—"}</strong>
      </p>
      <ArrivalFields
        id={id}
        currency={currency}
        values={values}
        set={set}
        errors={errors}
        fixed={fixed}
        wholeSum={multi}
      />
      {collect && collect.others.length > 0 && !fixed && (
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Other open invoices of this client
          </legend>
          <p className="text-xs text-muted-foreground">
            Tick those the same money settles: a payment is recorded on each, the sum received is
            shared out between them, and one receipt is issued for them all.
          </p>
          {collect.others.map((other) => {
            const label = other.invoice_no ?? "Invoice";
            return (
              <div key={other.id} className="grid gap-2 md:grid-cols-3 md:items-end">
                <Checkbox
                  id={`${id}-also-${other.id}`}
                  label={`${label} · balance ${formatMoney(other.balance, currency)}`}
                  checked={other.id in alsoSettles}
                  onChange={(on) => toggle(other, on)}
                />
                {other.id in alsoSettles && (
                  <TextField
                    id={`${id}-also-${other.id}-amount`}
                    label={`Amount for ${label}`}
                    inputMode="decimal"
                    value={alsoSettles[other.id] ?? ""}
                    onChange={(value) =>
                      setAlsoSettles((current) => ({ ...current, [other.id]: value }))
                    }
                  />
                )}
              </div>
            );
          })}
          {multi && (
            <p className="text-sm">
              Total applied: <strong>{formatMoney(centsToAmount(takenCents), currency)}</strong>
            </p>
          )}
          {canWaive && lastOther && (
            <Checkbox
              id={`${id}-waive`}
              label={`Waive the ${formatMoney(centsToAmount(shortCents), currency)} shortfall (written off on ${lastOther.invoice_no ?? "the last invoice"})`}
              checked={waive}
              onChange={setWaive}
            />
          )}
        </fieldset>
      )}
      {currency !== "INR" && (
        <RupeeValue
          id={`${id}-rupees`}
          currency={currency}
          net={wholeNet}
          receivedOn={values.received_on}
          receivedCurrency={values.received_currency}
          receivedRate={values.received_rate}
          receivedRateFixed={fixed}
          fxRate={values.fx_rate}
          inrCredited={values.inr_credited}
          error={errors.fx_rate}
          onChange={(rates) => setValues((current) => ({ ...current, ...rates }))}
        />
      )}
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Bank credit (can be added later from the eFIRA)
        </legend>
        <div className="grid gap-4 md:grid-cols-2">
          {accountOptions && (
            <SelectField
              id={`${id}-into`}
              label="Received into"
              value={values.received_into_id}
              onChange={(value) => set("received_into_id", value)}
              options={accountOptions}
              placeholder="Not specified"
              error={errors.received_into_id}
              hint="Which money account it landed in"
            />
          )}
          <TextField
            id={`${id}-inr`}
            label="INR credited"
            inputMode="decimal"
            value={values.inr_credited}
            onChange={(value) => set("inr_credited", value)}
            error={errors.inr_credited}
            hint={
              impliedRate
                ? `INR credited ÷ net received = ${impliedRate}`
                : "What the bank credited, as on the eFIRA; optional"
            }
          />
          <TextField
            id={`${id}-fira`}
            label="eFIRA / FIRC number"
            value={values.fira_ref}
            onChange={(value) => set("fira_ref", value)}
            error={errors.fira_ref}
          />
          <TextField
            id={`${id}-purpose`}
            label="Purpose code"
            value={values.purpose_code}
            onChange={(value) => set("purpose_code", value)}
            error={errors.purpose_code}
            maxLength={5}
            autoCapitalize="characters"
            hint="As reported by the bank, e.g. P1006 - confirm with your CA"
          />
          <TextField
            id={`${id}-reference`}
            label="Bank reference"
            value={values.reference}
            onChange={(value) => set("reference", value)}
            error={errors.reference}
            hint="SWIFT / UTR reference, optional"
          />
        </div>
      </fieldset>
      <TextAreaField
        id={`${id}-notes`}
        label="Notes"
        value={values.notes}
        onChange={(value) => set("notes", value)}
        error={errors.notes}
        rows={2}
      />
      <FormActions
        saving={saving}
        submitLabel={multi ? "Record payments and issue receipt" : submitLabel}
        onCancel={onCancel}
      />
    </form>
  );
}
